import {
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useState
} from "react";

import { CognitoUser } from "@aws-amplify/auth";
import GraphQLOrganizationDAOFactory from "../../lib/organization/graphQL/GraphQLOrganizationDAOFactory";
import GraphQLUserOrganizationAssociationDAOFactory from "../../lib/organization/graphQL/GraphQLOrganizationAssociationDAOFactory";
import { OrganizationType } from "../../models";
import { useQuery } from "react-query";

type UserContextType = {
    user: CognitoUser;
    accountType: OrganizationType | null | undefined;
    isLoading: boolean;
    organizationId: string | null | undefined;
    userAttributes: Record<string, string> | null;
};

const UserContext = createContext<UserContextType | null>(null);
const userOrganizationAssociationDAO = GraphQLUserOrganizationAssociationDAOFactory.getInstance();
const organizationDAO = GraphQLOrganizationDAOFactory.getInstance();

export const UserProvider = ({ children, user }: { children: ReactNode; user: CognitoUser; }) => {
    const [userAttributes, setUserAttributes] = useState<Record<string, string> | null>(null);
    const { data, isLoading } = useQuery<{ accountType: OrganizationType; organizationId: string } | null>({
        queryKey: ['accountType', user],
        queryFn: async () => {
            const organizationAssociations = await userOrganizationAssociationDAO.listUserOrganizationAssociationsByUserId(user.getUsername());
            if (organizationAssociations.length === 0) {
                return null;
            }
            const organization = await organizationDAO.getById(organizationAssociations[0].organizationId);
            return {
                accountType: organization.type as OrganizationType,
                organizationId: organization.id
            };
        },
        staleTime: Infinity,
        retry: false
    });

    useEffect(() => {
        if (user) {
            user.getUserAttributes((err, attributes) => {
                if (err) {
                    console.error(err);
                    return;
                }
                if (attributes) {
                    setUserAttributes(Object.fromEntries(attributes.map((attribute) => [attribute.Name, attribute.Value])));
                }
            });
        }
    }, [user]);

    return (
        <>
            <UserContext.Provider
                value={{
                    user,
                    accountType: data?.accountType,
                    organizationId: data?.organizationId,
                    isLoading,
                    userAttributes
                }}>
                {children}
            </UserContext.Provider>
        </>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context == undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};